// Dynamic Field Renderer Component
// Rendert beliebige Formularfelder basierend auf ihrem Datentyp

interface DynamicFieldRendererProps {
  fields: Record<string, any>;
  labels: Record<string, string>;
}

export default function DynamicFieldRenderer({ fields, labels }: DynamicFieldRendererProps) {
  if (!fields || Object.keys(fields).length === 0) {
    return (
      <p className="text-gray-500 italic">Keine zusätzlichen Felder vorhanden</p>
    );
  }

  const renderValue = (value: any): React.ReactNode => {
    // Null/Undefined
    if (value === null || value === undefined) {
      return <span className="text-gray-400">—</span>;
    }

    // Boolean
    if (typeof value === 'boolean') {
      return (
        <span className={value ? 'text-green-600 font-medium' : 'text-gray-600'}>
          {value ? '✓ Ja' : '✗ Nein'}
        </span>
      );
    }

    // Array
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-400">Keine Einträge</span>;
      }

      return (
        <ul className="list-disc list-inside space-y-1">
          {value.map((item, idx) => (
            <li key={idx} className="text-gray-900">
              {typeof item === 'object' ? JSON.stringify(item) : String(item)}
            </li>
          ))}
        </ul>
      );
    }

    // Object (nicht Array)
    if (typeof value === 'object') {
      return (
        <pre className="bg-gray-50 p-3 rounded-lg text-sm overflow-x-auto">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    // String/Number (mit Zeilenumbrüchen)
    const stringValue = String(value);
    if (stringValue.includes('\n')) {
      return (
        <div className="whitespace-pre-wrap text-gray-900">
          {stringValue}
        </div>
      );
    }

    // Regular String/Number
    return <span className="text-gray-900">{stringValue}</span>;
  };

  return (
    <div className="space-y-6">
      {Object.entries(fields).map(([key, value]) => {
        const label = labels[key] || key;

        return (
          <div key={key} className="border-b border-gray-200 pb-6 last:border-0 last:pb-0">
            <dt className="text-sm font-semibold text-gray-700 mb-2">
              {label}
            </dt>
            <dd className="text-sm">
              {renderValue(value)}
            </dd>
          </div>
        );
      })}
    </div>
  );
}
